import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { reviews } from '@/mocks/comics';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9ot9viftmtnfcfh0dkg';

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormSubmitted(true);
      return;
    }

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormError('网络异常，请稍后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 韩国漫画网"
        description="韩国漫画网是一个专业的韩国漫画在线阅读平台。了解我们的品牌故事、读者口碑与联系方式。致力于为广大韩漫爱好者提供优质的阅读体验。"
        keywords="韩国漫画网,关于我们,品牌介绍,联系方式,韩漫平台"
        canonicalUrl="https://www.koreanmanhwa.com/about"
        ogTitle="关于我们 - 韩国漫画网"
        ogDescription="韩国漫画网是一个专业的韩国漫画在线阅读平台。了解我们的品牌故事、读者口碑与联系方式。"
        ogType="website"
        ogUrl="https://www.koreanmanhwa.com/about"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "关于我们 - 韩国漫画网",
            "description": "韩国漫画网是一个专业的韩国漫画在线阅读平台。",
            "url": "https://www.koreanmanhwa.com/about",
            "about": {
              "@type": "Organization",
              "name": "韩国漫画网",
              "description": "韩国漫画在线阅读平台，提供热门韩漫免费阅读服务"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.koreanmanhwa.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "关于我们",
                "item": "https://www.koreanmanhwa.com/about"
              }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "韩国漫画网的漫画是否免费？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "是的，韩国漫画网上的所有漫画作品均支持免费在线阅读，无需付费即可畅读海量韩漫内容。"
                }
              },
              {
                "@type": "Question",
                "name": "韩国漫画网有多少部漫画作品？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "韩国漫画网目前收录了超过1000部韩国漫画作品，涵盖恋爱、奇幻、动作、剧情、搞笑、悬疑等多种类型，并且每日持续更新。"
                }
              },
              {
                "@type": "Question",
                "name": "韩国漫画网多久更新一次？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "韩国漫画网坚持每日更新，确保读者能够第一时间看到最新章节，追更不停歇。"
                }
              }
            ]
          }
        ]}
      />

      <Navbar />
      <main className="min-h-screen">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-20 pb-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <section className="mb-16">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">
                <strong>关于我们</strong>
              </h1>
              <p className="text-sm text-foreground-500 mt-3 max-w-lg mx-auto">
                韩国漫画网，一个专注于为华语读者提供优质韩国漫画阅读体验的平台
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl font-bold text-foreground-950 mb-4 font-heading">
                  <strong>品牌故事</strong>
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    韩国漫画网成立于2024年，由一群热爱韩国漫画的资深读者共同创立。我们深知华语读者对高品质韩漫阅读体验的渴望，因此决心打造一个专注于韩国漫画的在线阅读平台。
                  </p>
                  <p>
                    在短短两年时间里，韩国漫画网从最初收录几十部作品的小站，发展成为了拥有<strong>超过1000部精选韩漫作品</strong>的知名平台。我们的团队持续跟踪韩国漫画市场的最新动态，第一时间引进热门作品，确保读者能够享受到最新、最全的韩漫内容。
                  </p>
                  <p>
                    我们始终坚持"<strong>为读者提供最佳阅读体验</strong>"的核心理念。从作品筛选到页面设计，从分类导航到搜索功能，每一个细节都经过精心打磨，力求让每一位来到韩国漫画网的读者都能找到心仪的作品，享受沉浸式的阅读时光。
                  </p>
                  <p>
                    未来，韩国漫画网将继续拓展作品库、优化阅读体验，并探索更多与读者互动的方式，致力于成为华语世界最受欢迎的韩国漫画阅读平台之一。
                  </p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=cozy%20reading%20nook%20with%20warm%20ambient%20lighting%2C%20bookshelf%20filled%20with%20comic%20books%2C%20comfortable%20armchair%2C%20soft%20golden%20hour%20light%2C%20peaceful%20atmosphere%2C%20reading%20aesthetic%2C%20warm%20neutral%20tones%2C%20minimal%20interior&width=800&height=600&seq=about-brand-story&orientation=landscape"
                  alt="韩国漫画网品牌故事"
                  title="韩国漫画网 - 品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="mb-16 py-12 bg-background-100 rounded-2xl px-6 md:px-10">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                <strong>读者口碑</strong>
              </h2>
              <p className="text-sm text-foreground-500 mt-2">来自真实读者的评价，是我们前进的动力</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {reviews.map((review) => (
                <div key={review.id} className="bg-background-50 rounded-xl p-5 border border-background-200/60">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{review.name}</p>
                      <p className="text-xs text-foreground-500">{review.date}</p>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-500 text-xs"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.comment}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                <strong>联系我们</strong>
              </h2>
              <p className="text-sm text-foreground-500 mt-2">如有任何问题或建议，欢迎随时与我们联系</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950">电子邮件</h3>
                    <p className="text-sm text-foreground-500 mt-0.5">contact@koreanmanhwa.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className="ri-customer-service-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950">在线客服</h3>
                    <p className="text-sm text-foreground-500 mt-0.5">工作日 9:00 - 18:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-secondary-600 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950">办公地址</h3>
                    <p className="text-sm text-foreground-500 mt-0.5">首尔特别市江南区德黑兰路123号</p>
                  </div>
                </div>
              </div>

              <div>
                {formSubmitted ? (
                  <div className="bg-accent-50 border border-accent-200 rounded-xl p-8 text-center">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-4">
                      <i className="ri-check-line text-2xl text-accent-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-foreground-950">发送成功！</h3>
                    <p className="text-sm text-foreground-500 mt-1">感谢您的留言，我们会尽快回复您。</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
                    {formError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600">
                        {formError}
                      </div>
                    )}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="site-honeypot"
                    />
                    <button
                      type="submit"
                      className="w-full py-2.5 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-all text-sm whitespace-nowrap cursor-pointer"
                    >
                      发送留言
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}